'use client';

import React from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import {
  isComponentBlock,
  shouldCenterBlockItems,
  shouldUseColumnLayout,
  splitBlock,
} from '@/lib/blockUtils';
import { EBlockType } from '@/lib/enums';
import { BlockType, IBlock, IBlockItem, ICtaItem } from '@/lib/types';
import { cn } from '@/lib/utils';

const BLOCK_GAP = 'gap-5 md:gap-[22px]';
const ITEM_BASE = 'w-full rounded-[20px] bg-white/10';
const FALLBACK_HEIGHT = 'h-[200px]';

const ITEM_CLASSES: Record<BlockType, string> = {
  article: 'h-[529px] flex-1 shrink-0 lg:h-[584px] lg:max-w-[50%]',
  contentsPreview: 'h-[529px] flex-1 shrink-0 lg:h-[584px] lg:max-w-[50%]',
  news: 'h-[529px] lg:h-[362px]',
  cta: 'h-[267px] max-w-[420px] rounded-[10px] lg:h-[322px]',
  image: 'h-[307px] max-w-[600px] rounded-[10px] lg:h-[362px]',
  video: 'h-[360px] max-w-[640px] rounded-[10px] lg:h-[340px]',
  dailyBonus: 'h-[428px]',
  progressBar: 'h-[100px]',
  html: 'h-[120px]',
  iframe: 'h-[200px] lg:h-[400px]',
  offer: 'h-[320px]',
};

const CTA_TEXT_ONLY_CLASSES = 'h-[54px] w-[365px] rounded-xl';

const getItemClasses = (type: BlockType, item?: IBlockItem): string => {
  const cta = item as ICtaItem | undefined;

  if (type === EBlockType.CTA && !cta?.assetUrl && !cta?.assetUrlMobile) {
    return CTA_TEXT_ONLY_CLASSES;
  }

  return ITEM_CLASSES[type] ?? FALLBACK_HEIGHT;
};

const ItemSkeleton: React.FC<{ type: BlockType; item?: IBlockItem }> = ({ type, item }) => (
  <Skeleton
    data-testid={`block-skeleton-item-${type}`}
    className={cn(ITEM_BASE, getItemClasses(type, item))}
  />
);

const BlockRowSkeleton: React.FC<{ row: IBlock }> = ({ row }) => {
  const { title, items, type, contentType } = row;

  return (
    <div className={cn('blockWrapper flex w-full flex-col', BLOCK_GAP)}>
      {title && (
        <div className="flex w-full items-center gap-8 md:gap-10">
          <div className="h-px flex-1 bg-white/20" />
          <Skeleton className="h-7 w-40 bg-white/15 md:h-10 md:w-64" />
          <div className="h-px flex-1 bg-white/20" />
        </div>
      )}
      <div
        className={cn(
          'blockItems flex w-full',
          !isComponentBlock(type) && BLOCK_GAP,
          shouldUseColumnLayout(type) ? 'flex-col' : 'flex-wrap',
          shouldCenterBlockItems(type) && 'justify-center'
        )}
      >
        {isComponentBlock(type) ? (
          <ItemSkeleton type={type} />
        ) : (
          items?.map((item: IBlockItem, index: number) => (
            <ItemSkeleton key={index} type={item.type || contentType || type} item={item} />
          ))
        )}
      </div>
    </div>
  );
};

export const BlockSkeleton: React.FC<{ block: IBlock }> = ({ block }) => (
  <div
    aria-hidden="true"
    data-testid="block-skeleton"
    className={cn('flex w-full flex-col', BLOCK_GAP)}
  >
    {splitBlock(block).map((row: IBlock, index: number) => (
      <BlockRowSkeleton key={index} row={row} />
    ))}
  </div>
);
